import type { CompanyFinance } from "@shaarp/common";

/**
 * The ratios of a company's filed accounts (Pappers) the ratios widget shows:
 * its cash, its solidity and how fast it pays and is paid.
 */

/** How a ratio's value reads. */
export type FinancialRatioUnit = "amount" | "percent" | "days";

/** The ratios shown, in the order they show. */
export const FINANCIAL_RATIOS = [
  { key: "cash", unit: "amount" },
  { key: "equity", unit: "amount" },
  { key: "debt", unit: "amount" },
  { key: "financialAutonomy", unit: "percent" },
  { key: "ebitdaMarginRate", unit: "percent" },
  { key: "returnOnEquity", unit: "percent" },
  { key: "workingCapitalDaysRevenue", unit: "days" },
  { key: "clientPaymentDays", unit: "days" },
  { key: "supplierPaymentDays", unit: "days" },
] as const satisfies readonly {
  key: keyof CompanyFinance;
  unit: FinancialRatioUnit;
}[];

export type FinancialRatioKey = (typeof FINANCIAL_RATIOS)[number]["key"];

export type FinancialRatio = {
  readonly key: FinancialRatioKey;
  readonly unit: FinancialRatioUnit;
  readonly value: number;
};

export type FinancialRatiosOfYear = {
  readonly year: number;
  readonly ratios: readonly FinancialRatio[];
};

/** The ratios a filed year gives, in display order. */
function ratiosOf(finance: CompanyFinance): FinancialRatio[] {
  return FINANCIAL_RATIOS.flatMap(({ key, unit }) => {
    const value = finance[key];
    return typeof value === "number" && Number.isFinite(value)
      ? [{ key, unit, value }]
      : [];
  });
}

/**
 * The ratios of the most recent filed year that gives any.
 *
 * @param finances The filed years, in any order.
 * @returns That year and its ratios, or null when no year gives one.
 */
export function latestFinancialRatios(
  finances: readonly CompanyFinance[] | null | undefined,
): FinancialRatiosOfYear | null {
  const byYear = [...(finances ?? [])].sort((a, b) => b.year - a.year);
  for (const finance of byYear) {
    const ratios = ratiosOf(finance);
    if (ratios.length > 0) return { year: finance.year, ratios };
  }
  return null;
}
